import React from 'react';
import type { SubmissionFailure } from '../hooks/submission-state';
import { DUPLICATE_PATIENT_MESSAGE } from '../hooks/submission-state';
import { assertNever } from '../utils/assert-never';

type Tone = 'warning' | 'error';

interface BodyLine {
  key: string;
  text: string;
}

interface Presentation {
  tone: Tone;
  title: string;
  bodyLines: BodyLine[];
}

const TONE_CLASSES: Record<
  Tone,
  { box: string; title: string; body: string }
> = {
  warning: {
    box: 'bg-amber-50 border-amber-200',
    title: 'text-amber-900',
    body: 'text-amber-800',
  },
  error: {
    box: 'bg-red-50 border-red-200',
    title: 'text-red-800',
    body: 'text-red-600',
  },
};

const APPOINTMENT_CONFLICT_LINES: BodyLine[] = [
  {
    key: 'slot-taken',
    text:
      'Looks like the clinician or time slot you picked was booked by '
      + 'someone else a moment ago.',
  },
  {
    key: 'rebook-hint',
    text:
      "Don't worry — your details are saved. Our team will reach out "
      + 'shortly to help you rebook, or you can pick another available slot '
      + 'now.',
  },
];

const presentFailure = (failure: SubmissionFailure): Presentation => {
  switch (failure.kind) {
    case 'appointment-conflict':
      return {
        tone: 'warning',
        title: 'Time slot just got booked',
        bodyLines: APPOINTMENT_CONFLICT_LINES,
      };
    case 'patient-not-found':
      return {
        tone: 'error',
        title: 'Booking not found',
        bodyLines: [{
          key: 'message',
          text: 'We could not find a booking matching that name and date of birth.',
        }],
      };
    case 'duplicate-patient':
      return {
        tone: 'error',
        title: 'We need to check your details',
        bodyLines: [{ key: 'duplicate-patient', text: DUPLICATE_PATIENT_MESSAGE }],
      };
    case 'fee-changed':
      return {
        tone: 'warning',
        title: 'Your fee has been updated',
        bodyLines: [{
          key: 'fee-changed',
          text:
            'Our fees changed while you were booking. Please review the '
            + 'updated fee below, then submit again to confirm.',
        }],
      };
    case 'slot-expired':
      return {
        tone: 'warning',
        title: 'That time has just passed',
        bodyLines: [{
          key: 'slot-expired',
          text:
            'The appointment time you selected is no longer available. '
            + 'Please pick another available time to continue.',
        }],
      };
    case 'date-unavailable':
      return {
        tone: 'warning',
        title: 'That date is no longer available',
        bodyLines: [{
          key: 'date-unavailable',
          text:
            'The date you selected no longer has any available appointment '
            + 'times. Please pick another available date to continue.',
        }],
      };
    case 'validation':
    case 'unknown':
      return {
        tone: 'error',
        title: 'Submission failed',
        bodyLines: [{ key: 'message', text: failure.message }],
      };
    default:
      return assertNever(failure, 'Unexpected submission failure');
  }
};

export interface SubmitErrorAlertProps {
  failure: SubmissionFailure;
}

/**
 * Accessible inline alert for submission failures (live region for
 * screen readers).
 */
export const SubmitErrorAlert = ({ failure }: SubmitErrorAlertProps) => {
  const presentation = presentFailure(failure);
  const toneClass = TONE_CLASSES[presentation.tone];
  const titleId = 'submit-error-alert-title';

  return (
    <div
      className={`rounded-xl p-3 flex items-start gap-2.5 border mb-2 ${
        toneClass.box
      }`}
      role="alert"
      aria-live="polite"
      aria-labelledby={titleId}
    >
      <span
        className="text-base leading-none mt-0.5 shrink-0"
        aria-hidden="true"
      >
        &#x26A0;&#xFE0F;
      </span>
      <div className="min-w-0 flex-1 space-y-1.5">
        <p
          id={titleId}
          className={`text-sm font-semibold ${toneClass.title}`}
        >
          {presentation.title}
        </p>
        {presentation.bodyLines.map(({ key, text }) => (
          <p
            key={key}
            className={`text-xs leading-relaxed ${toneClass.body}`}
          >
            {text}
          </p>
        ))}
      </div>
    </div>
  );
};
